import React, { useState, useEffect, useMemo } from 'react';
import { API_BASE_URL } from '../config';
import { useSearchParams } from 'react-router-dom';
import { safeParseDate } from '../utils/dateUtils';
import { getIsoWeekNumber } from '../utils/isoDates';
import GIPlanningSelectionHeader from '../components/GI/GIPlanningSelectionHeader';
import GIPlanningTable from '../components/GI/GIPlanningTable';
import GIDetailedPlanningTable from '../components/GI/GIDetailedPlanningTable';
import GIPlanningDetailsModal from '../components/GI/GIPlanningDetailsModal';
import GIMobileInterventionPointDayView from '../components/GI/GIMobileInterventionPointDayView';
import GIInterventionPointDaySchedule from '../components/GI/GIInterventionPointDaySchedule';
import GIPlanningStatusFilter from '../components/GI/GIPlanningStatusFilter';
import useGIPlanningDerivedData from '../hooks/useGIPlanningDerivedData';
import Select from '../components/ui/Select';
import useAnalytics from '../hooks/useAnalytics';
import {
  applyStateFilter,
  getFilteredTimeslots,
  getPlanningItemPromoterIds,
  normalizePlanningState
} from '../utils/giPlanningStateUtils';
import '../styles/diagonal-cells.css';
import Loading from '../components/Loading';
import { getAdminBadge } from '../utils/getAdminBadge';

function GIPlanningViewer({ fetchWithAuth, currentUser }) {
  const [searchParams, setSearchParams] = useSearchParams();

  const jail = searchParams.has('jail');

  // IDs de sélection extraits de l'URL (ignorer les autres query params pour la logique de refresh)
  const urlSelectionIds = useMemo(() => ({
    clientId: searchParams.get('clientId'),
    companyId: searchParams.get('companyId'),
    entityId: searchParams.get('entityId')
  }), [searchParams]);

  useAnalytics({ currentUser, module: 'GI_PLANNING' });
  const isAdmin = currentUser?.isadmin || currentUser?.issuperadmin;

  // State pour les données et sélections
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [clients, setClients] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [entities, setEntities] = useState([]);
  const [hierarchicalCompanies, setHierarchicalCompanies] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [includeAllEntities, setIncludeAllEntities] = useState(true);
  const [planningData, setPlanningData] = useState({
    campaigns: [],
    interventionPoints: [],
    promoters: [],
    companies: [],
    planning: [],
    clients: [],
  });
  const [loading, setLoading] = useState(true);
  // The filter lists come from two separate endpoints: a failure has to be visible, or the page
  // renders with empty filters and says nothing.
  const [filtersError, setFiltersError] = useState(null);
  const [planningLoading, setPlanningLoading] = useState(false);
  const [viewMode, setViewMode] = useState('weekly'); // 'weekly', 'overview' ou 'detailed'
  const [desktopMode, setDesktopMode] = useState('planning'); // 'planning' ou 'storeSchedule'
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [detailedViewMode, setDetailedViewMode] = useState('promoters'); // 'promoters' ou 'interventionPoints'
  const [displayMode, setDisplayMode] = useState('campaigns'); // 'campaigns' ou 'interventionPoints'

  // États pour la modale de détails
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [modalType, setModalType] = useState(null); // 'campaign', 'promoter', 'interventionPoint'

  // État pour le filtre des statuts
  const [selectedStates, setSelectedStates] = useState([]);

  // Filtres avancés (Campagne / Magasin)
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  // Multi-sélection: tableaux d'options { value, label, ... }
  const [selectedCampaignFilter, setSelectedCampaignFilter] = useState([]);
  const [selectedStoreFilter, setSelectedStoreFilter] = useState([]);

  // État pour gérer l'expand/collapse des clients (quand selectedCompany uniquement)
  const [expandedClients, setExpandedClients] = useState(new Set());
  // Admin-only: include inactive campaigns and missions
  const [includeInactive, setIncludeInactive] = useState(false);

  const { track } = useAnalytics({ currentUser, project: selectedClient?.label || selectedCompany?.label });

  // Planning filtré par magasin (si un magasin est sélectionné)
  const effectivePlanningData = useMemo(() => {
    if (!selectedStoreFilter || selectedStoreFilter.length === 0 || !planningData.planning?.length) {
      return planningData;
    }

    const storeIds = new Set(selectedStoreFilter.map(opt => opt.value));
    const filteredPlanning = planningData.planning.filter(item =>
      storeIds.has(item.MissionInterventionPointId)
    );

    if (!filteredPlanning.length) {
      return {
        ...planningData,
        planning: [],
        campaigns: [],
        interventionPoints: [],
        promoters: []
      };
    }

    const campaignIds = new Set(filteredPlanning.map(item => item.CampaignId));
    const interventionPointIds = new Set(filteredPlanning.map(item => item.MissionInterventionPointId));
    const promoterIds = new Set(
      filteredPlanning.flatMap((item) => getPlanningItemPromoterIds(item))
    );

    return {
      ...planningData,
      planning: filteredPlanning,
      campaigns: (planningData.campaigns || []).filter(c => campaignIds.has(c.Id)),
      interventionPoints: (planningData.interventionPoints || []).filter(p => interventionPointIds.has(p.Id)),
      promoters: (planningData.promoters || []).filter(p => promoterIds.has(p.Id))
    };
  }, [planningData, selectedStoreFilter]);

  // Derived datasets, columns and maps
  const {
    weeklyData,
    overviewData,
    weeklyInterventionPointsData,
    overviewInterventionPointsData,
    detailedData,
    interventionPointsData,
    detailedDates,
    weeklyColumns,
    dailyColumns,
    currentDate,
    campaignsMap,
    promotersMap,
    interventionPointsMap,
    tooManyInterventionPoints
  } = useGIPlanningDerivedData({
    planningData: effectivePlanningData,
    selectedStates,
    selectedWeek,
    selectedCampaign,
    selectedClient,
    selectedCompany,
    expandedClients,
    displayMode
  });

  // Hook pour détecter l'orientation mobile
  const [isMobilePortrait, setIsMobilePortrait] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkOrientation = () => {
      const isMobile = window.innerWidth <= 1024; // Taille mobile
      const isPortrait = window.innerHeight > window.innerWidth;
      setIsMobilePortrait(isMobile && isPortrait);
      setIsMobile(isMobile);
    };

    checkOrientation();
    window.addEventListener('resize', checkOrientation);
    window.addEventListener('orientationchange', checkOrientation);

    return () => {
      window.removeEventListener('resize', checkOrientation);
      window.removeEventListener('orientationchange', checkOrientation);
    };
  }, []);

  // Filtre des statuts via composant dédié

  // Initialiser les dates depuis l'URL ou utiliser les valeurs par défaut
  useEffect(() => {
    const urlStartDate = searchParams.get('startDate');
    const urlEndDate = searchParams.get('endDate');

    if (urlStartDate && urlEndDate) {
      setStartDate(safeParseDate(urlStartDate));
      setEndDate(safeParseDate(urlEndDate));
    } else {
      const now = new Date();
      const defaultStartDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const defaultEndDate = new Date(now.getFullYear(), now.getMonth() + 3, 0);
      setStartDate(defaultStartDate);
      setEndDate(defaultEndDate);

      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('startDate', defaultStartDate.toISOString().split('T')[0]);
      newSearchParams.set('endDate', defaultEndDate.toISOString().split('T')[0]);
      setSearchParams(newSearchParams);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Récupération des clients, companies et associations au chargement (une seule fois)
  useEffect(() => {
    const fetchClientsAndCompanies = async () => {
      // fetchWithAuth is re-created on every App render, so this effect re-runs: clear the
      // previous verdict or a stale banner outlives the failure it describes.
      setFiltersError(null);
      const unavailable = [];
      let accessDenied = false;

      try {
        // Récupération des clients
        const clientsResponse = await fetchWithAuth(`${API_BASE_URL}/api/dwh/gi/clients`);
        if (clientsResponse.ok) {
          const clientsData = await clientsResponse.json();
          const clientOptions = clientsData.clients.map(client => ({
            value: client.Id,
            label: client.Name
          }));
          setClients(clientOptions);
        } else {
          console.error("Failed to fetch clients");
          unavailable.push('Client');
          accessDenied = accessDenied || clientsResponse.status === 403;
        }

        // Récupération des companies avec structure hiérarchique
        const companiesResponse = await fetchWithAuth(`${API_BASE_URL}/api/dwh/gi/companies`);
        if (companiesResponse.ok) {
          const companiesData = await companiesResponse.json();

          // Créer les options pour le select Company (seulement les parents/groupes)
          const parentCompanyOptions = companiesData.data.hierarchical
            .filter(company => company.isGroup) // Seulement les parents
            .map(company => ({
              value: company.companyId,
              label: company.companyName
            }));
          setCompanies(parentCompanyOptions);

          const entities = companiesData.data.raw
            .filter(company => !company.isGroup) // Seulement les parents
            .map(company => ({
              value: company.CompanyId,
              label: company.CompanyName
            }));
          setEntities(entities);

          // Stocker la structure hiérarchique complète
          setHierarchicalCompanies(companiesData.data.hierarchical);
        } else {
          console.error("Failed to fetch companies");
          unavailable.push('Groupe et Entité');
          accessDenied = accessDenied || companiesResponse.status === 403;
        }

        if (unavailable.length > 0) {
          setFiltersError(
            `${accessDenied ? 'Vos droits ne permettent pas de charger' : 'Impossible de charger'} : ${unavailable.join(', ')}. Ces filtres restent vides.`
          );
        }

      } catch (error) {
        console.error("Error fetching clients and companies:", error);
        setFiltersError('Les filtres n\'ont pas pu être chargés.');
      } finally {
        setLoading(false);
      }
    };

    fetchClientsAndCompanies();
  }, [fetchWithAuth]);

  // Synchroniser client / company depuis l'URL quand les listes sont prêtes
  useEffect(() => {
    const { clientId, companyId } = urlSelectionIds;

    if (clientId && clients.length > 0) {
      const clientFromUrl = clients.find(c => c.value.toString() === clientId);
      if (clientFromUrl) {
        setSelectedClient(prev =>
          prev && prev.value === clientFromUrl.value ? prev : clientFromUrl
        );
      } else {
        // Si le client dans l'URL n'existe pas, le supprimer de l'URL
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete('clientId');
        setSearchParams(newSearchParams);
      }
    }

    if (companyId && companies.length > 0) {
      const companyFromUrl = companies.find(c => c.value.toString() === companyId);
      if (companyFromUrl) {
        setSelectedCompany(prev =>
          prev && prev.value === companyFromUrl.value ? prev : companyFromUrl
        );
      } else {
        // Si la company dans l'URL n'existe pas, la supprimer de l'URL
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete('companyId');
        setSearchParams(newSearchParams);
      }
    }
  }, [urlSelectionIds, clients, companies, searchParams, setSearchParams]);

  // Récupération de l'entityId depuis l'URL quand selectedCompany et hierarchicalCompanies sont prêts
  useEffect(() => {
    const { entityId } = urlSelectionIds;
    if (entityId && selectedCompany && hierarchicalCompanies.length > 0) {
      // Trouver l'entité dans les données hiérarchiques
      const selectedCompanyData = hierarchicalCompanies.find(
        company => company.companyId === selectedCompany.value
      );

      if (selectedCompanyData && selectedCompanyData.entities) {
        const entityFromUrl = selectedCompanyData.entities.find(
          entity => entity.companyId.toString() === entityId
        );

        if (entityFromUrl) {
          setSelectedEntity(prev =>
            prev && prev.value === entityFromUrl.companyId
              ? prev
              : {
                  value: entityFromUrl.companyId,
                  label: entityFromUrl.companyName
                }
          );
          setIncludeAllEntities(prev => (prev === false ? prev : false)); // Disable include all when specific entity selected
        } else {
          // Si l'entité dans l'URL n'existe pas, la supprimer de l'URL
          const newSearchParams = new URLSearchParams(searchParams);
          newSearchParams.delete('entityId');
          setSearchParams(newSearchParams);
        }
      }
    }
  }, [urlSelectionIds, selectedCompany, hierarchicalCompanies, searchParams, setSearchParams]);

  // Handler pour les changements de plage de dates
  const handleDateRangeChange = ({ startDate: newStartDate, endDate: newEndDate }) => {
    setStartDate(newStartDate);
    setEndDate(newEndDate);
    setPlanningData({ campaigns: [], interventionPoints: [], promoters: [], companies: [], planning: [], clients: [] });
    setSelectedCampaignFilter([]);
    setSelectedStoreFilter([]);

    setSelectedCampaign(null);
    setSelectedWeek(null);
    setViewMode('weekly'); // Retour à la vue hebdomadaire
    setDetailedViewMode('promoters'); // Réinitialiser la vue détaillée
    setDisplayMode('campaigns'); // Réinitialiser le mode d'affichage
    setExpandedClients(new Set()); // Réinitialiser les clients expandés

    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('startDate', newStartDate.toISOString().split('T')[0]);
    newSearchParams.set('endDate', newEndDate.toISOString().split('T')[0]);
    newSearchParams.delete('campaignId');
    newSearchParams.delete('interventionPointId');
    setSearchParams(newSearchParams);
  };

  // Handler pour la sélection du client
  const handleClientChange = (client) => {
    setSelectedClient(client);
    setPlanningData({ campaigns: [], interventionPoints: [], promoters: [], companies: [], planning: [], clients: [] });
    setSelectedCampaignFilter([]);
    setSelectedStoreFilter([]);

    setSelectedCampaign(null);
    setSelectedWeek(null);
    setViewMode('weekly');
    setDetailedViewMode('promoters');
    setDisplayMode('campaigns');
    setExpandedClients(new Set()); // Réinitialiser les clients expandés

    const newSearchParams = new URLSearchParams(searchParams);
    if (client) {
      newSearchParams.set('clientId', client.value);
    } else {
      newSearchParams.delete('clientId');
    }
    newSearchParams.delete('campaignId');
    newSearchParams.delete('interventionPointId');
    setSearchParams(newSearchParams);

    if (client) {
      track('gi_planning_viewer_client_select', {
        gi_client: client.label
      });
    }
  };

  // Handler pour la sélection de la company
  const handleCompanyChange = (company) => {
    setSelectedCompany(company);
    setSelectedEntity(null); // Reset entity selection when company changes
    setIncludeAllEntities(company ? true : false); // Default to include all entities if company selected
    setSelectedCampaignFilter([]);
    setSelectedStoreFilter([]);

    setPlanningData({ campaigns: [], interventionPoints: [], promoters: [], companies: [], planning: [], clients: [] });

    setSelectedCampaign(null);
    setSelectedWeek(null);
    setViewMode('weekly');
    setDetailedViewMode('promoters');
    setDisplayMode('campaigns');
    setExpandedClients(new Set()); // Réinitialiser les clients expandés

    const newSearchParams = new URLSearchParams(searchParams);

    if (company) {
      newSearchParams.set('companyId', company.value);
    } else {
      newSearchParams.delete('companyId');
    }
    // Supprimer entityId et filtres avancés car la sélection d'entité est réinitialisée
    newSearchParams.delete('entityId');
    newSearchParams.delete('campaignId');
    newSearchParams.delete('interventionPointId');
    setSearchParams(newSearchParams);

    if (company) {
      track('gi_planning_viewer_company_select', {
        gi_company: company.label
      });
    }
  };

  // Handler pour la sélection d'entité
  const handleEntityChange = (entity) => {

    setSelectedEntity(entity);
    setIncludeAllEntities(!entity); // If entity is selected, don't include all entities
    setSelectedCampaignFilter([]);
    setSelectedStoreFilter([]);

    // If we select an entity and no company is selected, select the related group for the chosen entity
    if (entity && !selectedCompany) {
      // Find the parent company for this entity
      const parentCompany = hierarchicalCompanies.find(company => company.entities.some(ent => ent.companyId === entity.value));

      if (parentCompany) {
        setSelectedCompany({
          value: parentCompany.companyId,
          label: parentCompany.companyName
        });

        // Update URL with company ID
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.set('companyId', parentCompany.companyId);
        newSearchParams.set('entityId', entity.value);
        setSearchParams(newSearchParams);
      }
    } else {
      // Standard URL update
      const newSearchParams = new URLSearchParams(searchParams);
      if (entity) {
        newSearchParams.set('entityId', entity.value);
      } else {
        newSearchParams.delete('entityId');
      }
      newSearchParams.delete('campaignId');
      newSearchParams.delete('interventionPointId');
      setSearchParams(newSearchParams);
    }

    setPlanningData({ campaigns: [], interventionPoints: [], promoters: [], companies: [], planning: [], clients: [] });

    setSelectedCampaign(null);
    setSelectedWeek(null);
    setViewMode('weekly');
    setDetailedViewMode('promoters');
    setDisplayMode('campaigns');
    setExpandedClients(new Set());

    if (entity) {
      track('gi_planning_viewer_entity_select', {
        gi_entity: entity.label
      });
    }
  };

  // Handler pour le toggle "Inclure toutes les entités"
  const handleIncludeAllEntitiesChange = (include) => {
    setIncludeAllEntities(include);
    if (include) {
      setSelectedEntity(null); // Clear specific entity selection
    }
    setSelectedCampaignFilter([]);
    setSelectedStoreFilter([]);

    setPlanningData({ campaigns: [], interventionPoints: [], promoters: [], companies: [], planning: [], clients: [] });

    setSelectedCampaign(null);
    setSelectedWeek(null);
    setViewMode('weekly');
    setDetailedViewMode('promoters');
    setDisplayMode('campaigns');
    setExpandedClients(new Set());
  };

  // Récupération des données de planning quand le client/company ou les dates changent
  useEffect(() => {
    const fetchPlanning = async () => {
      if ((!selectedClient && !selectedCompany) || !startDate || !endDate) {
        setPlanningData({ campaigns: [], interventionPoints: [], promoters: [], companies: [], planning: [], clients: [] });

        return;
      }

      try {
        setPlanningLoading(true);
        const formattedStartDate = startDate.toISOString().split('T')[0];
        const formattedEndDate = endDate.toISOString().split('T')[0];

        // Construire l'URL avec les nouveaux paramètres
        let url = `${API_BASE_URL}/api/dwh/gi/planning?startDate=${formattedStartDate}&endDate=${formattedEndDate}`;

        if (selectedClient)
          url += `&clientId=${selectedClient.value}`;

        if (selectedCompany) {
          if (selectedEntity) {
            // Si une entité spécifique est sélectionnée, utiliser son ID
            url += `&companyId=${selectedEntity.value}`;
          } else {
            // Sinon, utiliser la company parent avec includeEntities
            url += `&companyId=${selectedCompany.value}`;
            if (includeAllEntities) {
              url += `&includeEntities=true`;
            }
          }
        }
        // Admin-only: include inactive items if requested
        if (isAdmin && includeInactive) {
          url += `&includeInactive=true`;
        }

        const response = await fetchWithAuth(url);

        if (response.ok) {
          const data = await response.json();
          setPlanningData({
            campaigns: data.campaigns || [],
            interventionPoints: data.interventionPoints || [],
            promoters: data.promoters || [],
            companies: data.companies || [],
            planning: data.planning || [],
            clients: data.clients || [],
          });
        } else {
          console.error("Failed to fetch planning");
          setPlanningData({ campaigns: [], interventionPoints: [], promoters: [], companies: [], planning: [], clients: [] });

        }
      } catch (error) {
        console.error("Error fetching planning:", error);
        setPlanningData({ campaigns: [], interventionPoints: [], promoters: [], companies: [], planning: [], clients: [] });
      } finally {
        setPlanningLoading(false);
      }
    };

    fetchPlanning();
  }, [selectedClient, selectedCompany, selectedEntity, includeAllEntities, includeInactive, startDate, endDate, fetchWithAuth, isAdmin]);

  // Maps moved to hook

  // Options d'entités pour la company sélectionnée
  const entityOptions = useMemo(() => {
    if (!hierarchicalCompanies.length) {
      return [];
    }

    // Si aucun groupe n'est sélectionné, afficher toutes les entités disponibles
    if (!selectedCompany) {
      const allEntities = [];
      hierarchicalCompanies.forEach(company => {
        if (company.entities && company.entities.length > 0) {
          company.entities.forEach(entity => {
            allEntities.push({
              value: entity.companyId,
              label: entity.companyName
            });
          });
        }
      });
      return allEntities;
    }

    // Si un groupe est sélectionné, afficher uniquement ses entités
    const selectedCompanyData = hierarchicalCompanies.find(
      company => company.companyId === selectedCompany.value
    );

    if (!selectedCompanyData || !selectedCompanyData.entities) {
      return [];
    }

    return selectedCompanyData.entities.map(entity => ({
      value: entity.companyId,
      label: entity.companyName
    }));
  }, [selectedCompany, hierarchicalCompanies]);

  // Options pour filtres avancés (Campagnes / Magasin)
  const campaignFilterOptions = useMemo(() => {
    if (!effectivePlanningData.campaigns || effectivePlanningData.campaigns.length === 0) return [];
    return effectivePlanningData.campaigns.map(campaign => ({
      value: campaign.Id,
      label: campaign.Name
    }));
  }, [effectivePlanningData.campaigns]);

  const storeFilterOptions = useMemo(() => {
    if (!planningData.interventionPoints || planningData.interventionPoints.length === 0) return [];

    // Si une ou plusieurs campagnes sont sélectionnées, ne proposer que les magasins utilisés dans ces campagnes
    let allowedStoreIds = null;
    if (selectedCampaignFilter && selectedCampaignFilter.length > 0 && planningData.planning && planningData.planning.length > 0) {
      const selectedCampaignIds = new Set(selectedCampaignFilter.map(opt => opt.value));
      allowedStoreIds = new Set(
        planningData.planning
          .filter(item => selectedCampaignIds.has(item.CampaignId))
          .map(item => item.MissionInterventionPointId)
      );
    }

    return planningData.interventionPoints
      .filter(point => !allowedStoreIds || allowedStoreIds.has(point.Id))
      .map(point => ({
        value: point.Id,
        // Conserver le Name brut pour le filtrage tout en affichant la ville si dispo
        label: `${point.Name}${point.AddressCity ? ` - ${point.AddressCity}` : ''}`,
        name: point.Name
      }));
  }, [planningData.interventionPoints, planningData.planning, selectedCampaignFilter]);

  // Client grouping moved to utils/hook

  // Weekly data moved to hook

  // Unique weeks moved to hook

  // Overview data moved to hook

  // Detailed data moved to hook

  // Detailed intervention points moved to hook

  // detailedDates moved to hook

  // Unique dates moved to hook

  // Weekly intervention points moved to hook

  // Overview intervention points moved to hook

  // Guard moved to hook

  // Revenir au mode campagnes si trop de points d'intervention
  useEffect(() => {
    if (tooManyInterventionPoints && displayMode === 'interventionPoints') {
      setDisplayMode('campaigns');
    }
  }, [tooManyInterventionPoints, displayMode]);

  // Current week/date moved to hook

  // Columns moved to hook

  // Handlers pour les clics
  const handleCampaignClick = (campaignName) => {
    setSelectedCampaign(campaignName);
    setViewMode('detailed');
    setDetailedViewMode('promoters'); // Réinitialiser à la vue promoteurs
  };

  const handleInterventionPointClick = (interventionPointName) => {
    // Pour les points d'intervention, on peut créer une vue détaillée similaire
    // Pour l'instant, on garde le même comportement mais on pourrait l'étendre
    setSelectedCampaign(interventionPointName);
    setViewMode('detailed');
    setDetailedViewMode('promoters');
  };

  const handleWeekHeaderClick = (week) => {
    setSelectedWeek(week);
    setViewMode('overview');
  };

  const handleClientColumnClick = (clientName) => {
    // Trouver le client correspondant dans la liste des clients
    const targetClient = clients.find(client => client.label === clientName);

    if (targetClient) {
      // Ajouter le client au filtre actuel en gardant la company si elle existe
      setSelectedClient(targetClient);
      // Garder selectedCompany inchangé pour combiner les filtres

      // Réinitialiser les états de vue
      setPlanningData({ campaigns: [], interventionPoints: [], promoters: [], companies: [], planning: [], clients: [] });
      setSelectedCampaign(null);
      setSelectedWeek(null);
      setViewMode('weekly');
      setDetailedViewMode('promoters');
      setDisplayMode('campaigns');
      setExpandedClients(new Set()); // Réinitialiser les clients expandés

      // Mettre à jour l'URL (garder companyId si existant)
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('clientId', targetClient.value);
      // Ne PAS supprimer companyId pour permettre le filtrage combiné
      setSearchParams(newSearchParams);

      // Analytics
      track('gi_planning_viewer_client_column_click', {
        gi_client: clientName,
        from_company: selectedCompany?.label,
        combined_filter: !!selectedCompany
      });
    }
  };

  const handleCompanyColumnClick = (companyName, companyId) => {
    // Trouver la company correspondante dans la liste des companies
    const targetCompany = companies.find(company => company.value === companyId);

    if (targetCompany) {
      // Ajouter la company au filtre actuel en gardant le client si il existe
      setSelectedCompany(targetCompany);
      setSelectedEntity(null); // Reset entity selection when company changes
      setIncludeAllEntities(true); // Default to include all entities

      // Garder selectedClient inchangé pour combiner les filtres

      // Réinitialiser les états de vue
      setPlanningData({ campaigns: [], interventionPoints: [], promoters: [], companies: [], planning: [], clients: [] });
      setSelectedCampaign(null);
      setSelectedWeek(null);
      setViewMode('weekly');
      setDetailedViewMode('promoters');
      setDisplayMode('campaigns');
      setExpandedClients(new Set()); // Réinitialiser les clients expandés

      // Mettre à jour l'URL (garder clientId si existant)
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('companyId', targetCompany.value);
      // Ne PAS supprimer clientId pour permettre le filtrage combiné
      setSearchParams(newSearchParams);

      // Analytics
      track('gi_planning_viewer_company_column_click', {
        gi_company: companyName,
        from_client: selectedClient?.label,
        combined_filter: !!selectedClient
      });
    }
  };

  const handleEntityColumnClick = (entityName, entityId, companyName, companyId) => {
    // Trouver la company correspondante
    const targetCompany = companies.find(company => company.value === companyId);

    if (targetCompany) {
      // Sélectionner la company
      setSelectedCompany(targetCompany);

      // Trouver l'entité dans les options disponibles
      const selectedCompanyData = hierarchicalCompanies.find(
        company => company.companyId === companyId
      );

      if (selectedCompanyData) {
        const targetEntity = selectedCompanyData.entities.find(
          entity => entity.companyId === entityId
        );

        if (targetEntity) {
          setSelectedEntity({
            value: targetEntity.companyId,
            label: targetEntity.companyName
          });
          setIncludeAllEntities(false); // Disable include all when specific entity selected
        }
      }

      // Réinitialiser les états de vue
      setPlanningData({ campaigns: [], interventionPoints: [], promoters: [], companies: [], planning: [], clients: [] });
      setSelectedCampaign(null);
      setSelectedWeek(null);
      setViewMode('weekly');
      setDetailedViewMode('promoters');
      setDisplayMode('campaigns');
      setExpandedClients(new Set());

      // Mettre à jour l'URL (garder clientId si existant)
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('companyId', targetCompany.value);
      newSearchParams.set('entityId', entityId.toString());
      // Ne PAS supprimer clientId pour permettre le filtrage combiné
      setSearchParams(newSearchParams);

      track('gi_planning_viewer_entity_column_click', {
        gi_entity: entityName,
        gi_company: companyName,
        from_client: selectedClient?.label
      });
    }
  };

  // Handler pour toggle expand/collapse des clients
  const handleClientToggle = (clientName) => {
    setExpandedClients(prev => {
      const newSet = new Set(prev);
      if (newSet.has(clientName)) {
        newSet.delete(clientName);
      } else {
        newSet.add(clientName);
      }
      return newSet;
    });
  };

  // Synchroniser les filtres avancés depuis l'URL (si présents) quand les options sont disponibles
  useEffect(() => {
    const raw = searchParams.get('campaignId');
    if (raw && campaignFilterOptions.length > 0) {
      const ids = raw.split(',').filter(Boolean);
      const options = campaignFilterOptions.filter(opt => ids.includes(String(opt.value)));
      setSelectedCampaignFilter(prev => {
        const prevIds = (prev || []).map(o => o.value).sort().join(',');
        const nextIds = options.map(o => o.value).sort().join(',');
        return prevIds === nextIds ? prev : options;
      });
    }
  }, [searchParams, campaignFilterOptions]);

  useEffect(() => {
    const raw = searchParams.get('interventionPointId');
    if (raw && storeFilterOptions.length > 0) {
      const ids = raw.split(',').filter(Boolean);
      const options = storeFilterOptions.filter(opt => ids.includes(String(opt.value)));
      setSelectedStoreFilter(prev => {
        const prevIds = (prev || []).map(o => o.value).sort().join(',');
        const nextIds = options.map(o => o.value).sort().join(',');
        return prevIds === nextIds ? prev : options;
      });
    }
  }, [searchParams, storeFilterOptions]);

  const handleCampaignFilterChange = (option) => {
    const options = option || [];
    setSelectedCampaignFilter(options);
    const newSearchParams = new URLSearchParams(searchParams);
    if (options.length > 0) {
      newSearchParams.set('campaignId', options.map(o => o.value).join(','));
    } else {
      newSearchParams.delete('campaignId');
    }
    setSearchParams(newSearchParams);
  };

  const handleStoreFilterChange = (option) => {
    const options = option || [];
    setSelectedStoreFilter(options);
    const newSearchParams = new URLSearchParams(searchParams);
    if (options.length > 0) {
      newSearchParams.set('interventionPointId', options.map(o => o.value).join(','));
    } else {
      newSearchParams.delete('interventionPointId');
    }
    setSearchParams(newSearchParams);
  };

  // Helper pour obtenir l'icône et la couleur selon le statut
  const getStateIconAndColor = (state) => {
    switch (state) {
      case 'Animé':
        return { icon: '✓', color: 'text-green-600', displayState: state };
      case 'Annulé':
        return { icon: '✗', color: 'text-red-600', displayState: state };
      case 'En attente':
        return { icon: '⏳', color: 'text-yellow-600', displayState: state };
      case null:
      case undefined:
      case '':
      case '__EMPTY__':
        return { icon: '--', color: 'text-gray-600', displayState: 'Non renseigné' };
      case 'Plusieurs statuts':
        return { icon: '±', color: 'text-gray-600', displayState: state };
      default:
        return { icon: '--', color: 'text-gray-600', displayState: state };
    }
  };

  const getVisibleTimeslots = (item) => getFilteredTimeslots(item, selectedStates);

  const getDecoratedTimeslots = (item, predicate = null) => (
    getVisibleTimeslots(item)
      .filter((timeslot) => (predicate ? predicate(timeslot) : true))
      .map((timeslot) => {
        const stateInfo = getStateIconAndColor(normalizePlanningState(timeslot.State));
        return {
          ...timeslot,
          state: stateInfo.displayState,
          stateIcon: stateInfo.icon,
          stateColor: stateInfo.color
        };
      })
  );

  const getAggregateStateInfo = (timeslots) => {
    const uniqueStates = [...new Set(
      timeslots.map((timeslot) => (
        typeof timeslot === 'string'
          ? normalizePlanningState(timeslot)
          : normalizePlanningState(timeslot.State || timeslot.state)
      ))
    )];

    if (uniqueStates.length === 0) {
      return getStateIconAndColor('__EMPTY__');
    }

    if (uniqueStates.length === 1) {
      return getStateIconAndColor(uniqueStates[0]);
    }

    return getStateIconAndColor('Plusieurs statuts');
  };

  // Données de tableau filtrées par filtres avancés (Campagne / Magasin)
  const tableData = useMemo(() => {
    const baseData = displayMode === 'campaigns'
      ? (viewMode === 'weekly' ? weeklyData : overviewData)
      : (viewMode === 'weekly' ? weeklyInterventionPointsData : overviewInterventionPointsData);

    let filtered = baseData;

    if (displayMode === 'campaigns' && selectedCampaignFilter && selectedCampaignFilter.length > 0) {
      const allowedNames = new Set(selectedCampaignFilter.map(opt => opt.label));
      filtered = filtered.filter(row => allowedNames.has(row.campaign));
    }

    return filtered;
  }, [
    displayMode,
    viewMode,
    weeklyData,
    overviewData,
    weeklyInterventionPointsData,
    overviewInterventionPointsData,
    selectedCampaignFilter
  ]);

  // Handler pour ouvrir la modale avec les détails
  const handleCellClick = (campaign, dateOrWeek, type, entityId = null) => {
    const dateKey = dateOrWeek;
    let data = [];

    if (type === 'campaign') {
      if (displayMode === 'interventionPoints') {
        // Pour la vue point d'intervention : récupérer campagnes et clients pour cette date/semaine
        let filteredPlanning = effectivePlanningData.planning.filter(item => {
          const itemInterventionPoint = interventionPointsMap.get(item.MissionInterventionPointId);
          if (!itemInterventionPoint || itemInterventionPoint.Name !== campaign) return false;

          if (viewMode === 'weekly') {
            const itemDate = new Date(item.Date);
            const itemWeek = getIsoWeekNumber(itemDate).fullWeekString;
            return itemWeek === dateKey;
          } else {
            return item.Date === dateKey;
          }
        });

        // Appliquer le filtre par statuts
        filteredPlanning = applyStateFilter(filteredPlanning, selectedStates);

        // Grouper par campagne et client avec comptage des promoteurs
        const campaignClientMap = new Map();

        filteredPlanning.forEach(item => {
          const itemCampaign = campaignsMap.get(item.CampaignId);
          const clientName = selectedCompany ? (item.Client || '') : (selectedClient?.label || '');
          const timeslots = getVisibleTimeslots(item);

          if (itemCampaign && timeslots.length > 0) {
            const key = `${itemCampaign.Name}|${clientName}`;

            if (!campaignClientMap.has(key)) {
              campaignClientMap.set(key, {
                campaign: itemCampaign.Name,
                client: clientName,
                promoters: new Set(),
                states: [],
                timeslots: [],
              });
            }

            const entry = campaignClientMap.get(key);
            timeslots.forEach((timeslot) => {
              if (timeslot.PromoterId) {
                entry.promoters.add(timeslot.PromoterId);
              }
              entry.states.push(normalizePlanningState(timeslot.State));
            });

            if (viewMode !== 'weekly') {
              entry.timeslots.push(...timeslots);
            }
          }
        });

        const campaignClientEntries = Array.from(campaignClientMap.values()).map((entry) => {
          const stateInfo = getAggregateStateInfo(entry.timeslots.length > 0 ? entry.timeslots : entry.states);
          return {
            entry: selectedCompany ? `${entry.campaign} (${entry.client}) - ${entry.promoters.size} promoteur${entry.promoters.size > 1 ? 's' : ''}`
                                   : `${entry.campaign} - ${entry.promoters.size} promoteur${entry.promoters.size > 1 ? 's' : ''}`,
            state: stateInfo.displayState,
            stateIcon: stateInfo.icon,
            stateColor: stateInfo.color,
            timeslots: entry.timeslots || []
          };
        });

        data = {
          entries: campaignClientEntries,
          campaign,
          date: dateKey,
          viewMode,
          isInterventionPointView: true
        };
      } else {
        // Pour la vue campagne : récupérer promoteurs et points d'intervention pour cette date/semaine
        let filteredPlanning = effectivePlanningData.planning.filter(item => {
          const itemCampaign = campaignsMap.get(item.CampaignId);
          if (!itemCampaign || itemCampaign.Name !== campaign) return false;

          if (viewMode === 'weekly') {
            const itemDate = new Date(item.Date);
            const itemWeek = getIsoWeekNumber(itemDate).fullWeekString;
            return itemWeek === dateKey;
          } else {
            return item.Date === dateKey;
          }
        });

        // Appliquer le filtre par statuts
        filteredPlanning = applyStateFilter(filteredPlanning, selectedStates);

        // Grouper promoteur et point d'intervention avec State
        const entriesMap = new Map();

        filteredPlanning.forEach(item => {
          const interventionPoint = interventionPointsMap.get(item.MissionInterventionPointId);
          if (!interventionPoint) return;

          getVisibleTimeslots(item).forEach((timeslot) => {
            const promoter = promotersMap.get(timeslot.PromoterId);
            if (!promoter) return;

            const entryKey = `${promoter.Name} (#${promoter.Id}) - ${interventionPoint.Name}`;

            if (!entriesMap.has(entryKey)) {
              entriesMap.set(entryKey, {
                entry: entryKey,
                states: [],
                timeslots: []
              });
            }

            entriesMap.get(entryKey).states.push(normalizePlanningState(timeslot.State));

            if (viewMode !== 'weekly') {
              entriesMap.get(entryKey).timeslots.push(timeslot);
            }
          });
        });

        const entriesWithState = Array.from(entriesMap.values()).map((entry) => {
          const stateInfo = getAggregateStateInfo(entry.timeslots.length > 0 ? entry.timeslots : entry.states);
          return {
            entry: entry.entry,
            state: stateInfo.displayState,
            stateIcon: stateInfo.icon,
            stateColor: stateInfo.color,
            timeslots: entry.timeslots
          };
        });

        data = {
          entries: entriesWithState,
          campaign,
          date: dateKey,
          viewMode
        };
      }
    } else if (type === 'promoter') {
      // Pour la vue promoteur : récupérer les timeslots
      const promoterId = entityId;
      let filteredPlanning = effectivePlanningData.planning.filter(item => {
        const itemCampaign = campaignsMap.get(item.CampaignId);
        return itemCampaign && itemCampaign.Name === selectedCampaign &&
               item.Date === dateKey &&
               getVisibleTimeslots(item).some((timeslot) => timeslot.PromoterId === promoterId);
      });

      // Appliquer le filtre par statuts
      filteredPlanning = applyStateFilter(filteredPlanning, selectedStates);

      // Récupérer les timeslots avec State
      const timeslotsWithState = filteredPlanning.flatMap((item) => (
        getDecoratedTimeslots(item, (timeslot) => timeslot.PromoterId === promoterId)
      ));

      data = {
        timeslots: timeslotsWithState,
        promoter: promotersMap.get(promoterId)?.Name,
        date: dateKey,
        campaign: selectedCampaign
      };
    } else if (type === 'interventionPoint') {
      // Pour la vue point d'intervention : récupérer promoteurs et timeslots
      const interventionPointName = entityId;
      let filteredPlanning = effectivePlanningData.planning.filter(item => {
        const itemCampaign = campaignsMap.get(item.CampaignId);
        const itemInterventionPoint = interventionPointsMap.get(item.MissionInterventionPointId);
        return itemCampaign && itemCampaign.Name === selectedCampaign &&
               itemInterventionPoint && itemInterventionPoint.Name === interventionPointName && item.Date === dateKey;
      });

      // Appliquer le filtre par statuts
      filteredPlanning = applyStateFilter(filteredPlanning, selectedStates);

      const promotersMapWithTimeslots = new Map();

      filteredPlanning.forEach((item) => {
        getDecoratedTimeslots(item).forEach((timeslot) => {
          const promoterId = timeslot.PromoterId || 'unknown';

          if (!promotersMapWithTimeslots.has(promoterId)) {
            promotersMapWithTimeslots.set(promoterId, {
              promoter: promotersMap.get(timeslot.PromoterId)?.Name || 'Promoteur inconnu',
              timeslots: []
            });
          }

          promotersMapWithTimeslots.get(promoterId).timeslots.push(timeslot);
        });
      });

      const promotersWithTimeslots = Array.from(promotersMapWithTimeslots.values()).map((entry) => {
        const stateInfo = getAggregateStateInfo(entry.timeslots);
        return {
          promoter: entry.promoter,
          timeslots: entry.timeslots,
          state: stateInfo.displayState,
          stateIcon: stateInfo.icon,
          stateColor: stateInfo.color
        };
      });

      data = {
        promotersWithTimeslots,
        interventionPoint: interventionPointName,
        date: dateKey,
        campaign: selectedCampaign
      };
    }

    setModalData(data);
    setModalType(type);
    setIsModalOpen(true);
  };

  return (
    <div className={`${isMobile ? 'w-full min-w-full' : 'container mx-auto'} ${isMobile ? 'p-0' : 'p-4 md:p-2 lg:p-4'} relative overflow-x-hidden`}>

      {/* Header avec la sélection de client et dates */}
      <div className={isMobile ? 'hidden' : ''}>
        <GIPlanningSelectionHeader
          currentUser={currentUser}
          startDate={startDate}
          endDate={endDate}
          clients={clients}
          companies={companies}
          planningData={planningData}
          selectedClient={selectedClient}
          selectedCompany={selectedCompany}
          selectedEntity={selectedEntity}
          entityOptions={entityOptions}
          includeAllEntities={includeAllEntities}
          onDatesChange={handleDateRangeChange}
          onClientChange={handleClientChange}
          onCompanyChange={handleCompanyChange}
          onEntityChange={handleEntityChange}
          onIncludeAllEntitiesChange={handleIncludeAllEntitiesChange}
        />
      </div>

      {filtersError && (
        <div
          role="alert"
          className="mx-2 md:mx-0 mb-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-700/60 dark:bg-amber-900/20 dark:text-amber-200"
        >
          {filtersError}
        </div>
      )}

      {/* Indicateur de chargement */}
      {loading && (
        <div className="flex justify-center my-8">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      )}

      {!loading && !isMobile && (
        <div className="mb-4 md:mb-6 mx-2 md:mx-0">
          <div className="inline-flex items-center bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
            <button
              type="button"
              onClick={() => setDesktopMode('planning')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                desktopMode === 'planning'
                  ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
              }`}
            >
              Planning
            </button>
            <button
              type="button"
              onClick={() => setDesktopMode('storeSchedule')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                desktopMode === 'storeSchedule'
                  ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
              }`}
            >
              Schedule magasin
            </button>
          </div>
        </div>
      )}

      {/* Message si aucun client/company sélectionné */}
      {!loading && desktopMode === 'planning' && !selectedClient && !selectedCompany && !isMobile && (
        <div className={`bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 ${isMobile ? 'rounded-none' : 'rounded-lg'} p-4 md:p-6 mb-4 md:mb-6 ${isMobile ? 'mx-0' : 'mx-2 md:mx-0'} flex items-center justify-center`}>
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-6 w-6 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-3 text-center flex flex-col items-center justify-center">
              <h3 className="text-lg font-medium text-blue-800 dark:text-blue-300 text-center">
                Sélectionnez un Client un Groupe ou une Entité pour commencer
              </h3>
              <div className="mt-2 text-blue-700 dark:text-blue-200 text-center">
                <p>
                  Veuillez sélectionner un Client ou une Groupe pour afficher le planning des campagnes GI associées. Les plannings sont actuellement sychronisés toutes les nuits avec la base de données de GI.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {!loading && !isMobile && desktopMode === 'storeSchedule' && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 lg:p-6 mx-2 md:mx-0">
          <div className="mb-4">
            <h2 className="text-xl font-bold text-gray-800 dark:text-white">Schedule magasin</h2>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              Vue operationnelle par point d'intervention, independante des filtres client/groupe.
            </p>
          </div>
          <GIInterventionPointDaySchedule fetchWithAuth={fetchWithAuth} layout="desktop" />
        </div>
      )}

      {/* Contenu principal */}
      {!loading && desktopMode === 'planning' && (selectedClient || selectedCompany) && (
        <div className={`bg-white dark:bg-gray-800 ${isMobile ? 'rounded-none' : 'rounded-lg'} shadow-md ${isMobile ? 'p-2' : 'p-2 md:p-4 lg:p-6'} ${isMobile ? 'mx-0' : 'mx-2 md:mx-0'}`}>
          {/* En-tête avec boutons de vue */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 md:mb-6 gap-4 md:gap-0">
            <div>
              <h2 className="text-xl font-bold text-gray-800 dark:text-white">
                Planning - {selectedClient && selectedCompany
                  ? `${selectedClient.label} × ${selectedCompany.label}`
                  : selectedClient
                    ? selectedClient.label
                    : selectedCompany?.label}
              </h2>
              {selectedCampaign && viewMode === 'detailed' && (
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {selectedCampaign}
                </p>
              )}
            </div>

            <div className="flex flex-col md:flex-row items-stretch md:items-center gap-2 md:gap-4 w-full md:w-auto">
              {/* Navigation avec breadcrumb */}
              <div className="flex items-center gap-2">

                {/* Bouton retour à la vue overview */}
                {viewMode === 'detailed' && (
                  <button
                    onClick={() => {
                      setViewMode('overview');
                      setSelectedCampaign(null);
                      setDetailedViewMode('promoters');
                    }}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
                  >
                    <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    Vue générale
                  </button>
                )}
              </div>

              {/* Toggle vue + Indicateurs */}
              <div className="flex flex-col md:flex-row items-stretch md:items-center gap-2 md:gap-4">
                {/* Toggle entre vue hebdomadaire et vue par jour */}
                {viewMode !== 'detailed' && (
                  <>
                    <div className="flex items-center bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                      <button
                        onClick={() => {
                          setViewMode('weekly');
                          setSelectedWeek(null);
                          setSelectedCampaign(null);
                          setDetailedViewMode('promoters');
                        }}
                        className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                          viewMode === 'weekly'
                            ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                            : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                        }`}
                      >
                        Semaines
                      </button>
                      <button
                        onClick={() => {
                          setViewMode('overview');
                          setDetailedViewMode('promoters');
                        }}
                        className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                          viewMode === 'overview'
                            ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                            : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                        }`}
                      >
                        Jours
                      </button>
                    </div>

                    <div className="flex items-center bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                      <button
                        onClick={() => setDisplayMode('campaigns')}
                        className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                          displayMode === 'campaigns'
                            ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                            : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                        }`}
                      >
                        <svg className="w-4 h-4 mr-1 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                        <span className="hidden md:inline">Campagnes</span>
                        <span className="md:hidden">Camp.</span>
                      </button>
                      <button
                        onClick={() => !tooManyInterventionPoints && setDisplayMode('interventionPoints')}
                        disabled={tooManyInterventionPoints}
                        className={`px-3 py-1 text-sm font-medium rounded-md transition-colors relative ${
                          tooManyInterventionPoints
                            ? 'bg-gray-200 dark:bg-gray-800 text-gray-400 dark:text-gray-600 cursor-not-allowed opacity-50'
                            : displayMode === 'interventionPoints'
                              ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                        }`}
                        title={tooManyInterventionPoints ? `Trop de points d'intervention (${planningData.interventionPoints.length} > 500). Vue désactivée pour des raisons de performance.` : ''}
                      >
                        <svg className="w-4 h-4 mr-1 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <span className="hidden md:inline">Points</span>
                        <span className="md:hidden">Pts</span>
                        {tooManyInterventionPoints && (
                          <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full flex items-center justify-center">
                            <span className="text-white text-xs font-bold">!</span>
                          </span>
                        )}
                      </button>
                    </div>
                  </>
                )}

                {/* Filtre des statuts */}
                <div className="w-full md:min-w-[200px] md:w-auto relative z-10">
                  <GIPlanningStatusFilter
                    selectedStates={selectedStates}
                    onChange={setSelectedStates}
                  />
                </div>

                {/* Indicateur du niveau et nombre d'éléments */}
                <div className="flex flex-wrap items-center gap-2 justify-center md:justify-start">
                  {selectedWeek && (
                    <div className="bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 py-1 px-3 rounded-full text-xs font-medium">
                      {selectedWeek}
                    </div>
                  )}
                  <div className="bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 py-1 px-3 rounded-full text-sm">
                    {planningLoading ? (
                      'Chargement...'
                    ) : viewMode === 'weekly' ? (
                      displayMode === 'campaigns'
                        ? `${tableData.length} campagne${tableData.length > 1 ? 's' : ''}`
                        : `${tableData.length} point${tableData.length > 1 ? 's' : ''} d'intervention`
                    ) : viewMode === 'overview' ? (
                      displayMode === 'campaigns'
                        ? `${tableData.length} campagne${tableData.length > 1 ? 's' : ''}`
                        : `${tableData.length} point${tableData.length > 1 ? 's' : ''} d'intervention`
                    ) : (
                      detailedViewMode === 'promoters'
                        ? `${detailedData.length} promoteur${detailedData.length > 1 ? 's' : ''}`
                        : `${interventionPointsData.length} point${interventionPointsData.length > 1 ? 's' : ''} d'intervention`
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Filtres avancés Campagnes / Magasin */}
          {planningData.planning.length > 0 && viewMode !== 'detailed' && !jail && (
            <div className="mb-4">
              <div className="flex items-center justify-end mb-2">
                <button
                  type="button"
                  onClick={() => setShowAdvancedFilters(v => !v)}
                  className="inline-flex items-center text-sm text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                >
                  <span className="mr-2">Filtres avancés</span>
                  <svg
                    className={`w-4 h-4 transition-transform ${showAdvancedFilters ? 'transform rotate-180' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </div>

              {showAdvancedFilters && (
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-white dark:bg-gray-800">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 text-left mb-1.5">
                        Campagne
                      </label>
                      <Select
                        isMulti
                        isClearable
                        isSearchable
                        value={selectedCampaignFilter}
                        onChange={handleCampaignFilterChange}
                        options={campaignFilterOptions}
                        placeholder="Filtrer par campagne..."
                        classNamePrefix="react-select"
                        menuPortalTarget={document.body}
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 text-left mb-1.5">
                        Magasin
                      </label>
                      <Select
                        isMulti
                        isClearable
                        isSearchable
                        value={selectedStoreFilter}
                        onChange={handleStoreFilterChange}
                        options={storeFilterOptions}
                        placeholder="Filtrer par magasin..."
                        classNamePrefix="react-select"
                        menuPortalTarget={document.body}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Tabs pour la vue détaillée */}
          {viewMode === 'detailed' && (
            <div className="mb-4">
              <div className="flex items-center bg-gray-100 dark:bg-gray-700 rounded-lg p-1 overflow-x-auto">
                <button
                  onClick={() => setDetailedViewMode('promoters')}
                  className={`px-3 md:px-4 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
                    detailedViewMode === 'promoters'
                      ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                  }`}
                >
                  <svg className="w-4 h-4 mr-1 md:mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  <span className="hidden md:inline">Promoteurs</span>
                  <span className="md:hidden">Promo.</span> ({detailedData.length})
                </button>
                <button
                  onClick={() => setDetailedViewMode('interventionPoints')}
                  className={`px-3 md:px-4 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
                    detailedViewMode === 'interventionPoints'
                      ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                  }`}
                >
                  <svg className="w-4 h-4 mr-1 md:mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span className="hidden md:inline">Points d'intervention</span>
                  <span className="md:hidden">Points</span> ({interventionPointsData.length})
                </button>
              </div>
            </div>
          )}

          {/* Message d'information si trop de points d'intervention */}
          {tooManyInterventionPoints && (
            <div className="mb-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-amber-800 dark:text-amber-200">
                    <span className="font-medium">Vue par points d'intervention désactivée.</span>
                    {' '}Trop de points d'intervention ({planningData.interventionPoints.length} &gt; 500) pour des raisons de performance.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Indicateur de chargement pour les données */}
          {planningLoading ? (
            <div className="my-8">
              <Loading
                size="lg"
                message="Chargement du planning..."
                showFunFact={true}
                className="py-8"
              />
            </div>
          ) : planningData.planning.length > 0 ? (
            <div>
              {viewMode === 'detailed' ? (
                <GIDetailedPlanningTable
                  isMobile={isMobile}
                  detailedViewMode={detailedViewMode}
                  detailedData={detailedData}
                  interventionPointsData={interventionPointsData}
                  detailedDates={detailedDates}
                  currentDate={currentDate}
                  handleCellClick={handleCellClick}
                  selectedCampaign={selectedCampaign}
                />
              ) : (
                <GIPlanningTable
                  data={tableData}
                  columns={viewMode === 'weekly' ? weeklyColumns : dailyColumns}
                  onCampaignClick={displayMode === 'campaigns' ? handleCampaignClick : handleInterventionPointClick}
                  onColumnHeaderClick={viewMode === 'weekly' ? handleWeekHeaderClick : undefined}
                  isMobile={isMobile}
                  headerLabel={displayMode === 'campaigns' ? "Campagne" : "Point d'intervention"}
                  selectedCompany={selectedCompany}
                  selectedClient={selectedClient}
                  displayMode={displayMode}
                  handleClientColumnClick={handleClientColumnClick}
                  handleCompanyColumnClick={handleCompanyColumnClick}
                  handleEntityColumnClick={handleEntityColumnClick}
                  handleCellClick={handleCellClick}
                  handleClientToggle={handleClientToggle}
                  currentUser={currentUser}
                />
              )}
            </div>
          ) : (
            <div className="text-center py-10 text-gray-500 dark:text-gray-400">
              <p>Aucun planning trouvé pour cette période.</p>
            </div>
          )}
          {isAdmin && (
            <div className="mt-4 flex justify-end">
              <label className="inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={includeInactive}
                  onChange={(e) => setIncludeInactive(e.target.checked)}
                />
                <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:bg-blue-600">
                  <div className="absolute top-[2px] left-[2px] bg-white border border-gray-300 rounded-full h-5 w-5 transition-all peer-checked:translate-x-full peer-checked:border-white"></div>
                </div>
                <span className="ml-3 text-sm text-gray-700 dark:text-gray-300">
                  {getAdminBadge()} &nbsp;Afficher les campagnes et missions inactives
                </span>
              </label>
            </div>
          )}
        </div>
      )}

      {/* Mode mobile IP/jour: si mobile portrait et aucun filtre client/company sélectionné, afficher la vue jour IP */}
      {!loading && isMobile && !selectedClient && !selectedCompany && (
        <div className="p-2">
          <GIMobileInterventionPointDayView fetchWithAuth={fetchWithAuth} />
        </div>
      )}

      {/* Modale pour afficher les détails */}
      <GIPlanningDetailsModal
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        modalData={modalData}
        modalType={modalType}
        selectedStates={selectedStates}
      />

      {/* Overlay d'orientation mobile (gardé mais non bloquant ailleurs) */}
      {/* <MobileOrientationOverlay isMobilePortrait={isMobilePortrait} /> */}
    </div>
  );
}

export default GIPlanningViewer;